import "./Subscriptions.scss";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { ConfigProvider, DatePicker } from "antd";
import { DataGrid } from "@mui/x-data-grid";
import { SubscriptionsColumns } from "../../SourceData";
import api from "../../utils/api";
import ar_EG from "antd/es/locale/ar_EG"; // Arabic locale
import "dayjs/locale/ar"; // Import Arabic locale for dayjs
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import { toast } from "react-toastify";
import { usePermissions } from "./../../hooks/usePermissions";

const { RangePicker } = DatePicker;

const Subscriptions = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");

  // State variables
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(10); // Default value: "الجميع"

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Services data state
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);

  // Add state for new filters
  const [hasDiscount, setHasDiscount] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [fetch, setFetch] = useState(false);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Only update searchTerm if query is empty or has more than 2 characters
    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      // Reset to first page when filters change
      setPage(1);
    }
  };

  // Update the fetchServices function to include date range parameters

  // Effect to fetch services when dependencies change
  useEffect(() => {
    const fetchServices = async () => {
      // Check view permission before fetching data
      if (!hasPermission(128)) {
        return;
      }

      setLoading(true);
      try {
        let params = {
          page,
          limit: pageSize,
        };
        // Add search term if it's not empty
        if (searchTerm) {
          params.q = searchTerm;
          // params.local = "ar";
        }
        // Add category filter if not "الجميع" (All)
        if (selectedStatus !== 10) {
          params.type = selectedStatus;
        }
        // Add date range parameters if selected
        if (dateRange && dateRange[0] && dateRange[1]) {
          // Format dates as YYYY-MM-DD
          params.startDate = dateRange[0].format("YYYY-MM-DD");
          params.endDate = dateRange[1].format("YYYY-MM-DD");
        }
        // Add hasDiscount filter if checked
        if (hasDiscount) {
          params.hasDiscount = "true";
        }
        if (isActive) {
          params.isActive = "false";
        }

        const response = await api.get("/subscriptions", { params });

        if (response.data.status === "success") {
          setServices(response.data.data);
          setTotalCount(response.data.totalCount);
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, [
    page,
    pageSize,
    searchTerm,
    dateRange,
    hasDiscount,
    selectedStatus,
    isActive,
    fetch,
  ]);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleActive = async (id) => {
    // Check activation permission before proceeding
    if (!hasPermission(129)) {
      toast.error("ليس لديك صلاحية لتفعيل/إلغاء تفعيل الاشتراكات");
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/subscriptions/${id}/toggleStatus`);
      toast.success("تم تغير الحالة بنجاح");
      setFetch(!fetch);
    } catch (error) {
      toast.error("حدث خطأ أثناء تغيير حالة الاشتراك");
    } finally {
      setLoading(false);
    }
  };

  const actionColumn = [
    {
      field: "isActive",
      headerName: "الحالة",
      minWidth: 80,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.isActive === true;
        // Check if user has activation permission to show the switch
        const canActivate = hasPermission(129);

        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row._id)}
            disabled={!canActivate}
            style={{
              opacity: canActivate ? 1 : 0.5,
              cursor: canActivate ? "pointer" : "not-allowed",
            }}
          />
        );
      },
    },
  ];

  return (
    <div className="products">
      <div className="special-header">
        <p className="title">
          الاشتراكات <span className="count-badge">{totalCount || 0}</span>
        </p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث باسم العميل او رقم الجوال...."
              onChange={handleSearchChange}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <ConfigProvider locale={ar_EG} direction="rtl">
              <RangePicker
                format={"DD MMM"}
                style={{
                  width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                  padding: "0px 10px",
                }}
                placeholder={["من", "الي"]}
                className="range-picker"
                onChange={handleDateRangeChange}
                value={dateRange}
                popupStyle={{ direction: "rtl" }}
                inputReadOnly={true}
              />
            </ConfigProvider>
          </div>
        </div>

        <div className="filters-container">
          <div className="filters">
            <FormControl
              sx={{
                width: isMobile ? "100%" : "180px",
              }}
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                نوع الاشتراك
              </InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                displayEmpty={true}
                sx={{ borderRadius: "8px", height: "48px" }}
              >
                <MenuItem value={10}>الجميع</MenuItem>
                <MenuItem value="annually">سنوي</MenuItem>
                <MenuItem value="monthly">شهري</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
        <div className="checked-container">
          <div className="item">
            <input
              type="checkbox"
              checked={hasDiscount}
              onChange={(e) => setHasDiscount(e.target.checked)}
            />
            <label>الطلبات التي تحتوي على خصم</label>
          </div>
          <div className="item">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <label>الاشتركات المنتهية</label>
          </div>
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={SubscriptionsColumns.concat(actionColumn)}
          rows={services.map((service, index) => ({
            ...service,
            // Generate unique id if not present
            id: service._id || index,
            // Add index for numbering
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            // Handle page change
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            // Handle page size change
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1); // Reset to first page when changing page size
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "طلب في الصفحة",
            },
          }}
        />
      </div>
    </div>
  );
};

export default Subscriptions;
